import { Badge } from 'core/components/ui/Badge'
import { hasPermission } from 'core/auth/rbac'
import { FilterBar } from 'core/components/ui/FilterBar'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { catalogueRepository } from 'features/catalogue/data/repositories/CatalogueRepositoryImpl'
import { useEffect, useState } from 'react'
import { OrderListItem, OrderStatus } from '../../domain/entities/Order'
import { ClubIndentsModal } from '../components/ClubIndentsModal'
import { IndentDetailModal } from '../components/IndentDetailModal'
import { IndentsTable } from '../components/IndentsTable'
import { useIndents } from '../hooks/useIndents'

const getItemUnits = (order: OrderListItem) =>
  order.totalUnits || order.items?.reduce((sum, item) => sum + item.units, 0) || 0

const fetchCatalogMoq = async (order?: OrderListItem, catalogItemId?: string): Promise<number> => {
  // 1. Check if the order item already carries the catalog item MOQ from backend
  const itemMoq = order?.items?.[0]?.moq
  if (itemMoq && itemMoq > 0) {
    return itemMoq
  }

  // 2. Query catalogue repository (which now returns soft-deleted items as well)
  const catId = catalogItemId || order?.items?.[0]?.catalogItemId
  if (catId) {
    try {
      const catItem = await catalogueRepository.getCatalogItemById(catId)
      if (catItem && catItem.moq > 0) {
        return catItem.moq
      }
    } catch {
      // ignore
    }
  }

  return 1000
}

export function IndentsPage() {
  const { user } = useAuth()
  const canPlaceOrder = hasPermission(user, 'update_order_status')

  const {
    orders,
    loading,
    error,
    search,
    setSearch,
    catalogItemId,
    setCatalogItemId,
    limit,
    setLimit,
    page,
    setPage,
    totalPages,
    totalItems,
    updateStatus,
    resetFilters,
    refresh,
  } = useIndents(25)

  const [selectedOrder, setSelectedOrder] = useState<OrderListItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // Catalog products for product filter selector
  const [catalogProducts, setCatalogProducts] = useState<{ id: string; name: string; variant?: string }[]>([])

  useEffect(() => {
    let isMounted = true
    catalogueRepository
      .getCatalogItems({ page: 1, limit: 100, status: 'active' })
      .then((res) => {
        if (isMounted) {
          setCatalogProducts(
            res.items.map((it) => ({
              id: it.id,
              name: it.name,
              variant: it.variant,
            })),
          )
        }
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
  }, [])

  // Persistent Selection state: Map<orderId, OrderListItem> preserves selected indents across pages & searches
  const [selectedOrdersMap, setSelectedOrdersMap] = useState<Map<string, OrderListItem>>(new Map())
  const [productMoq, setProductMoq] = useState<number>(1000)
  const [warningMessage, setWarningMessage] = useState<string>('')
  const [trayOpen, setTrayOpen] = useState<boolean>(false)

  // Club modal state
  const [clubModalOpen, setClubModalOpen] = useState(false)

  const selectedOrders = Array.from(selectedOrdersMap.values())
  const selectedIds = new Set(selectedOrdersMap.keys())
  const totalSelectedUnits = selectedOrders.reduce((sum, o) => sum + getItemUnits(o), 0)
  const activeProduct = selectedOrders[0]?.items?.[0]
  const activeCatalogItemId = activeProduct?.catalogItemId
  const isMoqReached = totalSelectedUnits >= productMoq && totalSelectedUnits > 0
  const progressPct = Math.min(100, Math.round((totalSelectedUnits / Math.max(1, productMoq)) * 100))

  const handleReviewClick = (order: OrderListItem) => {
    setSelectedOrder(order)
    setDetailOpen(true)
  }

  const handlePlaceOrder = (order: OrderListItem) => {
    if (!canPlaceOrder) return
    handleToggleSelect(order)
  }

  const handleToggleSelect = async (order: OrderListItem) => {
    if (!canPlaceOrder) return
    setWarningMessage('')
    const orderCatId = order.items?.[0]?.catalogItemId
    const nextOrdersMap = new Map(selectedOrdersMap)

    if (selectedOrdersMap.has(order.id)) {
      nextOrdersMap.delete(order.id)
      setSelectedOrdersMap(nextOrdersMap)
      if (nextOrdersMap.size === 0) {
        setProductMoq(1000)
        setTrayOpen(false)
      }

      return
    }

    // If selecting first item
    if (selectedOrdersMap.size === 0) {
      nextOrdersMap.set(order.id, order)
      setSelectedOrdersMap(nextOrdersMap)
      const moq = await fetchCatalogMoq(order, orderCatId)
      setProductMoq(moq)

      return
    }

    // Check if new indent belongs to the same catalog product
    if (orderCatId && activeCatalogItemId && orderCatId !== activeCatalogItemId) {
      setWarningMessage(
        `Selection restricted: Indents must belong to the same catalog product (${activeProduct?.productName || 'selected item'}) to be clubbed together.`,
      )
      return
    }

    nextOrdersMap.set(order.id, order)
    setSelectedOrdersMap(nextOrdersMap)
  }

  const handleToggleSelectAll = () => {
    if (!canPlaceOrder) return
    setWarningMessage('')
    if (orders.length === 0) return

    // Target the already active product if one is selected; otherwise target the first order's product on this page
    const targetCatId = activeCatalogItemId || orders[0]?.items?.[0]?.catalogItemId

    const matchingOrdersOnPage = orders.filter((o) => {
      const catId = o.items?.[0]?.catalogItemId
      return !targetCatId || !catId || catId === targetCatId
    })

    if (matchingOrdersOnPage.length === 0) return

    // If all matching orders on this page are already in the selection map, deselect this page's matching orders
    const allMatchingSelected = matchingOrdersOnPage.every((o) => selectedOrdersMap.has(o.id))
    const nextToggleAllMap = new Map(selectedOrdersMap)

    if (allMatchingSelected) {
      matchingOrdersOnPage.forEach((o) => nextToggleAllMap.delete(o.id))
      setSelectedOrdersMap(nextToggleAllMap)
      if (nextToggleAllMap.size === 0) {
        setProductMoq(1000)
        setTrayOpen(false)
      }

      return
    }

    // Otherwise, add all matching orders on this page to selection
    matchingOrdersOnPage.forEach((o) => nextToggleAllMap.set(o.id, o))
    setSelectedOrdersMap(nextToggleAllMap)

    if (selectedOrdersMap.size === 0 && matchingOrdersOnPage[0]) {
      fetchCatalogMoq(matchingOrdersOnPage[0], targetCatId).then((moq) => {
        setProductMoq(moq)
      })
    }
  }

  const handleRemoveFromSelection = (id: string) => {
    const nextRemovalMap = new Map(selectedOrdersMap)
    nextRemovalMap.delete(id)
    setSelectedOrdersMap(nextRemovalMap)
    if (nextRemovalMap.size === 0) {
      setProductMoq(1000)
      setTrayOpen(false)
    }
  }

  const handleClearSelection = () => {
    setSelectedOrdersMap(new Map())
    setProductMoq(1000)
    setWarningMessage('')
    setTrayOpen(false)
    setCatalogItemId('')
  }

  const handleFilterToSelectedProduct = () => {
    if (activeCatalogItemId) {
      setCatalogItemId(activeCatalogItemId)
    }
  }

  const handleUpdateStatus = async (nextStatus: OrderStatus) => {
    if (!selectedOrder) return
    // Let concurrency conflicts / fetch errors propagate to the modal so it can display them.
    await updateStatus(selectedOrder.id, nextStatus, selectedOrder.version)
    setDetailOpen(false)
    setSelectedOrder(null)
  }

  const handlePoReleased = () => {
    setSelectedOrdersMap(new Map())
    setProductMoq(1000)
    setTrayOpen(false)
    setClubModalOpen(false)
    setCatalogItemId('')
    refresh()
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Indents"
        description="Review cattle feed indents, track cumulative units against Catalogue MOQ, and release merged manufacturer Purchase Orders."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Indents' }]}
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Product Mismatch Warning Banner */}
      {warningMessage && (
        <div className="p-3 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 rounded-btn font-medium flex items-center justify-between">
          <span>{warningMessage}</span>
          <button onClick={() => setWarningMessage('')} className="text-xs underline font-bold ml-2">
            Dismiss
          </button>
        </div>
      )}

      {/* Persistent Selection Summary & MOQ Progress Bar */}
      {canPlaceOrder && selectedOrders.length > 0 && (
        <div className="border border-border bg-surface rounded-card p-4 space-y-3 shadow-sm transition-all">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-primary uppercase tracking-wider">
                  Selected Product: {activeProduct?.productName || 'Cattle Feed SKU'} (
                  {activeProduct?.variant || activeProduct?.unit || 'Bag'})
                </span>
                {activeProduct?.catalogItemDeleted && (
                  <Badge tone="warning" variant="subtle" size="sm" className="font-bold">
                    Delisted
                  </Badge>
                )}
                {activeCatalogItemId && catalogItemId !== activeCatalogItemId && (
                  <button
                    onClick={handleFilterToSelectedProduct}
                    className="text-[11px] font-semibold text-primary underline hover:text-primary-hover flex items-center gap-1 cursor-pointer"
                    title="Filter table to only show indents for this product"
                  >
                    <span>Filter list to this product</span>
                  </button>
                )}
                {activeCatalogItemId && catalogItemId === activeCatalogItemId && (
                  <button
                    onClick={() => setCatalogItemId('')}
                    className="text-[11px] font-semibold text-text-secondary hover:text-text-primary underline flex items-center gap-1 cursor-pointer"
                    title="Clear product filter and show all products"
                  >
                    <span>Filtered to product (Show all)</span>
                  </button>
                )}
              </div>
              <p className="text-xs text-text-secondary mt-0.5">
                <span className="font-semibold text-text-primary">{selectedOrders.length}</span> indent
                {selectedOrders.length > 1 ? 's' : ''} selected across pages •{' '}
                <span className="font-semibold text-text-primary">{totalSelectedUnits}</span> Total Units
              </p>
            </div>

            <div className="flex items-center justify-end gap-2.5 flex-wrap">
              <span className={`text-sm font-extrabold ${isMoqReached ? 'text-status-active' : 'text-primary'}`}>
                {totalSelectedUnits} / {productMoq} Units
              </span>

              <button
                type="button"
                onClick={() => setTrayOpen(!trayOpen)}
                className="px-2.5 py-1.5 text-xs font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1"
              >
                <span>
                  {trayOpen ? 'Hide Batch' : 'Review Batch'} ({selectedOrders.length})
                </span>
                <svg
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${trayOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={handleClearSelection}
                className="px-2.5 py-1.5 text-xs font-semibold border border-border rounded-btn bg-surface hover:bg-danger/10 hover:text-danger hover:border-danger/30 text-text-secondary transition-colors"
                title="Deselect all indents"
              >
                Clear
              </button>

              <button
                onClick={() => setClubModalOpen(true)}
                disabled={!isMoqReached}
                className="px-4 py-2 text-xs font-bold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn shadow transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                {isMoqReached ? 'Raise Manufacturer PO' : `Need ${productMoq - totalSelectedUnits} More Units`}
              </button>
            </div>
          </div>

          <div className="h-2 rounded-full bg-border overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${isMoqReached ? 'bg-status-active' : 'bg-primary'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Tray Drawer: Selected items across pages */}
          {trayOpen && (
            <div className="pt-2 border-t border-border border-primary/5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-text-secondary mb-2">
                Indents in this Clubbing Batch ({selectedOrders.length})
              </div>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                {selectedOrders.map((order) => {
                  const units = getItemUnits(order)
                  const buyerName = order.buyer?.businessName || order.buyer?.name || 'Retailer'
                  return (
                    <div
                      key={order.id}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-surface border border-border rounded-btn text-xs shadow-xs"
                    >
                      <span className="font-mono font-semibold text-text-primary text-[11px]">{order.id}</span>
                      <span className="text-text-secondary text-[11px] max-w-[120px] truncate" title={buyerName}>
                        {buyerName}
                      </span>
                      <Badge tone="primary" variant="subtle" size="sm" className="font-bold text-[10px] px-1 py-0">
                        {units} Units
                      </Badge>
                      <button
                        type="button"
                        onClick={() => handleRemoveFromSelection(order.id)}
                        className="text-text-secondary hover:text-danger ml-0.5 p-0.5 rounded transition-colors"
                        title="Remove from batch"
                        aria-label={`Remove order ${order.id}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search order ID, buyer, address or contact..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={Boolean(search || catalogItemId)}
        onReset={resetFilters}
      >
        {/* Product SKU Filter */}
        <select
          value={catalogItemId}
          onChange={(e) => setCatalogItemId(e.target.value)}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none focus:border-primary"
          aria-label="Filter by product"
        >
          <option value="">All Catalog Products</option>
          {catalogProducts.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} {p.variant ? `(${p.variant})` : ''}
            </option>
          ))}
        </select>

        {/* Page Size Selector */}
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-2.5 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none focus:border-primary"
          aria-label="Items per page"
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </FilterBar>

      {/* Table Data */}
      <IndentsTable
        orders={orders}
        loading={loading}
        onReview={handleReviewClick}
        onPlaceOrder={handlePlaceOrder}
        canPlaceOrder={canPlaceOrder}
        selectedIds={selectedIds}
        activeCatalogItemId={activeCatalogItemId}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={limit}
        emptyMessage={
          catalogItemId
            ? 'No indents are currently below MOQ for the selected product.'
            : 'No indents are currently below MOQ.'
        }
      />

      {/* Details Side-drawer modal */}
      <IndentDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        order={selectedOrder}
        onUpdateStatus={handleUpdateStatus}
      />

      {/* Club indents into one bulk purchase order once MOQ is reached */}
      <ClubIndentsModal
        isOpen={clubModalOpen}
        onClose={() => setClubModalOpen(false)}
        selectedOrders={selectedOrders}
        productMoq={productMoq}
        onOrdersRaised={handlePoReleased}
      />
    </div>
  )
}
